import { useEffect, useState, useCallback, useRef } from 'react';
import {
  ConsentDialogState,
  UseConsentDialogReturn,
  ConsentVerificationResponse,
  CONSENT_TEXT,
  CONSENT_TYPE,
  DIALOG_DISPLAY_DELAY,
  ERROR_MESSAGES,
  CONSENT_DIALOG_EVENTS,
} from './types';
import { PrescriptionService } from '../service';
import { validateEmail } from './emailValidation';
import { TrackerEventService } from '../trackerEventService';
import { trackingService } from '../../../shared/eventTracking';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';
import { get } from 'lodash';

interface UseConsentDialogProps {
  doctorId: number;
  prescriberEmail?: string;
  prescriptionId?: number;
  onClose?: () => void;
}

export function useConsentDialog({
  doctorId,
  prescriberEmail = '',
  prescriptionId,
  onClose,
}: UseConsentDialogProps): UseConsentDialogReturn {
  const [state, setState] = useState<ConsentDialogState>({
    isVisible: false,
    isLoading: true,
    email: prescriberEmail,
    emailError: null,
    apiError: null,
    isSubmitting: false,
  });

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const { prescriberConfiguration } = usePrescriptionStore();
  const disablePromotionalOptIn = get(prescriberConfiguration, 'Features.disablePromotionalOptIn', false);

  const trackEvent = useCallback(
    async (eventName: string, eventBody?: Record<string, any>) => {
      if (!prescriptionId) {
        return;
      }

      try {
        await TrackerEventService.track(prescriptionId, eventName, {
          doctorId,
          ...eventBody,
        });
      } catch (error) {
      }
    },
    [prescriptionId, doctorId]
  );

  useEffect(() => {
    let isMounted = true;

    const verifyConsent = async () => {
      try {
        
        const response: ConsentVerificationResponse = await PrescriptionService.fetchConsent(
          doctorId,
          CONSENT_TYPE
        );
        

        if (!isMounted) return;

        if (!!response.RequererConsentimento && !disablePromotionalOptIn) {
          timerRef.current = setTimeout(() => {
            if (isMounted) {
              setState((prev) => ({
                ...prev,
                isVisible: true,
                isLoading: false,
              }));
              
              trackEvent(CONSENT_DIALOG_EVENTS.DIALOG_DISPLAYED, {
                consentType: CONSENT_TYPE,
              });
            }
          }, DIALOG_DISPLAY_DELAY);
        } else {
          setState((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      } catch (error) {
        if (isMounted) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      }
    };

    verifyConsent();

    return () => {
      isMounted = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [doctorId, trackEvent]);

  const handleEmailChange = useCallback((email: string) => {
    setState((prev) => ({
      ...prev,
      email,
      emailError: null,
      apiError: null,
    }));
  }, []);

  const handleAccept = useCallback(async () => {
    const validation = validateEmail(state.email);
    
    if (!validation.isValid) {
      setState((prev) => ({
        ...prev,
        emailError: validation.error || ERROR_MESSAGES.VALIDATION.EMAIL_INVALID,
      }));
      return;
    }

    trackingService.trackConsentDialogConsent();

    setState((prev) => ({
      ...prev,
      isSubmitting: true,
      emailError: null,
      apiError: null,
    }));

    try {
      await PrescriptionService.updateConsent(
        doctorId,
        prescriptionId || 0,
        CONSENT_TEXT,
        true,
        CONSENT_TYPE,
        state.email.trim()
      );

      await trackEvent(CONSENT_DIALOG_EVENTS.USER_ACCEPTED, {
        consentType: CONSENT_TYPE,
        email: state.email.trim(),
      });

      setState((prev) => ({
        ...prev,
        isVisible: false,
        isSubmitting: false,
      }));

      if (onClose) {
        onClose();
      }
    } catch (error) {      
      await trackEvent(CONSENT_DIALOG_EVENTS.ERROR_OCCURRED, {
        consentType: CONSENT_TYPE,
        action: 'accept',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      
      setState((prev) => ({
        ...prev,
        isSubmitting: false,
        apiError: ERROR_MESSAGES.API.CONSENT_SUBMISSION,
      }));
    }
  }, [state.email, doctorId, prescriptionId, onClose, trackEvent]);

  const handleRefuse = useCallback(async () => {
    trackingService.trackConsentDialogLater();

    setState((prev) => ({
      ...prev,
      isSubmitting: true,
      apiError: null,
    }));

    try {
      await PrescriptionService.refuseConsent(doctorId, CONSENT_TYPE);
      
      await trackEvent(CONSENT_DIALOG_EVENTS.USER_REFUSED, {
        consentType: CONSENT_TYPE,
      });
    } catch (error) {      
      await trackEvent(CONSENT_DIALOG_EVENTS.ERROR_OCCURRED, {
        consentType: CONSENT_TYPE,
        action: 'refuse',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setState((prev) => ({
        ...prev,
        isVisible: false,
        isSubmitting: false,
      }));

      if (onClose) {
        onClose();
      }
    }
  }, [doctorId, onClose, trackEvent]);

  const handleClose = useCallback(async () => {
    trackingService.trackConsentDialogClose();

    setState((prev) => ({
      ...prev,
      isSubmitting: true,
      apiError: null,
    }));

    try {
      await PrescriptionService.refuseConsent(doctorId, CONSENT_TYPE);
      
      await trackEvent(CONSENT_DIALOG_EVENTS.USER_REFUSED, {
        consentType: CONSENT_TYPE,
      });
    } catch (error) {
      
    } finally {
      setState((prev) => ({
        ...prev,
        isVisible: false,
        isSubmitting: false,
      }));

      if (onClose) {
        onClose();
      }
    }
  }, [doctorId, onClose, trackEvent]);

  return {
    state,
    actions: {
      handleEmailChange,
      handleAccept,
      handleRefuse,
      handleClose,
    },
  };
}
